<script lang="ts" setup>
import * as Sentry from '@sentry/vue'
import { computed, nextTick, onBeforeUnmount, onErrorCaptured, onMounted, ref, watch } from 'vue'
import { useTolgee, useTranslate } from '@tolgee/vue'
import { MapboxMap, MapboxMarker } from '@studiometa/vue-mapbox-gl'
import mapboxgl, { type MapEventOf } from 'mapbox-gl'
import Decimal from 'decimal.js'
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  MultiPolygon,
  Point,
  Polygon,
} from 'geojson'
import 'mapbox-gl/dist/mapbox-gl.css'

import type {
  IComponentMediaAsset,
  ImpactColor,
  ImpactType,
  IPlantingSession,
  IPlasticCollection,
  IProject,
  IProjectEvidence,
} from '@/api/interfaces'
import { toGalleryItems, useSharedMediaGallery } from '@/composables/useSharedMediaGallery'
import { IMPACT_TYPE_MAP_PIN_COLOR_MAP } from '@/constants'
import { getFeatureCoordinates, haversineMeters, mergeFeatureCollections } from '@/helpers/geojson'
import type { IMapPin } from '@/helpers/interfaces'
import { formatTotalAmounts } from '@/helpers/utils'
import collectionPinIcon from '@/assets/icons/collection-pin.svg'
import imagePinIcon from '@/assets/icons/image-pin.svg'
import plantingSitePinIcon from '@/assets/icons/planting-site-pin.svg'

import PublicMapPin from '@/components/public-page/PublicMapPin.vue'

const DEFAULT_MAP_CENTER: [number, number] = [0, 16]
const DEFAULT_MAP_ZOOM = 1.2
const DEFAULT_MAX_ZOOM = 18
const DEFAULT_COLOR = '#3b755f'
const MAP_STYLE = import.meta.env.VITE_MAPBOX_STYLE_URL
const IMAGE_STACK_DISTANCE_METERS = 10
const PROJECT_FOCUS_DURATION_MS = 3600

const SELECTED_EVIDENCE_SOURCE_ID = 'selected-evidence-source'
const SELECTED_EVIDENCE_LINE_LAYER_ID = 'selected-evidence-line-layer'
const SELECTED_EVIDENCE_FILL_LAYER_ID = 'selected-evidence-fill-layer'
const SELECTED_EVIDENCE_POINT_LAYER_ID = 'selected-evidence-point-layer'

const IMAGE_CLUSTER_SOURCE_ID = 'pins-clusters'
const IMAGE_CLUSTER_LAYER_ID = 'pins-clusters-circle'
const IMAGE_CLUSTER_COUNT_LAYER_ID = 'pins-clusters-count'
const IMAGE_UNCLUSTERED_LAYER_ID = 'pins-unclustered'
const IMAGE_UNCLUSTERED_STACK_COUNT_LAYER_ID = 'pins-unclustered-stack-count'
const IMAGE_UNCLUSTERED_SINGLETON_LAYER_ID = 'pins-unclustered-singleton'
const IMAGE_PIN_ICON_ID = 'image-pin-icon'

const SITE_PINS_SOURCE_ID = 'site-pins-clusters'
const SITE_CLUSTER_LAYER_ID = 'site-pins-clusters-circle'
const SITE_CLUSTER_COUNT_LAYER_ID = 'site-pins-clusters-count'
const SITE_PINS_LAYER_ID = 'site-pins-unclustered'
const SITE_GEOFENCES_SOURCE_ID = 'selected-site-geofences'
const SITE_GEOFENCES_FILL_LAYER_ID = 'selected-site-geofences-fill'
const SITE_GEOFENCES_OUTLINE_LAYER_ID = 'selected-site-geofences-outline'
const PLANTING_SITE_PIN_ICON_ID = 'planting-site-pin-icon'

const PLASTIC_CLUSTER_SOURCE_ID = 'plastic-collection-clusters'
const PLASTIC_CLUSTER_LAYER_ID = 'plastic-collection-clusters-circle'
const PLASTIC_CLUSTER_COUNT_LAYER_ID = 'plastic-collection-clusters-count'
const PLASTIC_UNCLUSTERED_LAYER_ID = 'plastic-collection-unclustered'
const PLASTIC_COLLECTION_PIN_ICON_ID = 'plastic-collection-pin-icon'

const EMPTY_FEATURE_COLLECTION: FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
}

const { t } = useTranslate()
const tolgee = useTolgee(['language'])

interface IImageGroupItem {
  imageUrl?: string
  imagePreviewUrl?: string
  imageAlt?: string
  isVideo?: boolean
}

type IImagePinProperties = GeoJsonProperties & {
  type: 'image' | 'image-stack'
  fieldUpdateId?: number
  imageUrl?: string
  imagePreviewUrl?: string
  imageAlt?: string
  datePlanted?: string | null
  stackCount?: number
  imagesJson?: string
  isVideo?: boolean
}

type ISitePinProperties = GeoJsonProperties & {
  type: 'site'
  fieldUpdateId?: number
  siteId?: string
}

type IPlasticCollectionPinProperties = GeoJsonProperties & {
  type: 'plastic-collection'
  buyerName?: string
  totalBottles?: number
  collectionIndices?: string
}

interface Props {
  projects: IProject[]
  selectedProjectId: string | null
  selectedProjectEvidence?: IProjectEvidence
  selectedProjectPlantingGeoJson?: FeatureCollection[] | null
  selectedProjectPlasticCollectionGeoJson?: FeatureCollection[] | null
  colorSettings: Record<ImpactType, ImpactColor>
  focusRequestKey: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  selectProject: [projectId: string]
}>()

const map = ref<mapboxgl.Map | null>(null)
const imagePopup = ref<mapboxgl.Popup | null>(null)
const sitePopup = ref<mapboxgl.Popup | null>(null)
const collectionPopup = ref<mapboxgl.Popup | null>(null)
let rerenderOpenPopup: (() => void) | null = null
const hasFittedInitialBounds = ref(false)
const hasMapError = ref(false)
const mapShellRef = ref<HTMLElement | null>(null)
const allowMapMount = ref(false)
let resizeObserver: ResizeObserver | null = null
let webglContextLostHandler: ((e: MapEventOf<'webglcontextlost'>) => void) | undefined
let mapInitBreadcrumbLogged = false

const mapboxSupported = computed((): boolean => {
  try {
    return Boolean(mapboxgl.supported({ failIfMajorPerformanceCaveat: false }))
  } catch {
    return false
  }
})

const lastHandledFocusRequestKey = ref(0)
const hasImageClusterHandlers = ref(false)
const hasSiteClusterHandlers = ref(false)
const hasPlasticClusterHandlers = ref(false)
const sharedMediaGallery = useSharedMediaGallery()
const selectedProject = computed(() =>
  props.projects.find((project) => project.projectId === props.selectedProjectId),
)

const selectedProjectColor = computed(
  () =>
    (selectedProject.value
      ? props.colorSettings[selectedProject.value.type]?.backgroundColor
      : undefined) || DEFAULT_COLOR,
)

const clusterColor = computed(() => {
  const activeType = selectedProject.value?.type ?? props.projects[0]?.type
  return activeType ? IMPACT_TYPE_MAP_PIN_COLOR_MAP[activeType] || DEFAULT_COLOR : DEFAULT_COLOR
})

const projectPins = computed<IMapPin[]>(() =>
  props.projects.flatMap(
    (project, index) =>
      project.geoJSON?.features?.flatMap((feature) => {
        if (
          feature.geometry.type !== 'Point' ||
          !Array.isArray(feature.geometry.coordinates) ||
          feature.geometry.coordinates.length < 2
        ) {
          return []
        }

        const [longitude, latitude] = feature.geometry.coordinates

        if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
          return []
        }

        return [
          {
            index,
            country: project.countries?.[0] || project.projectId,
            type: project.type,
            projectId: project.projectId,
            availability: project.availability,
            link: project.link,
            pinLocation: {
              longitude,
              latitude,
            },
          },
        ]
      }) ?? [],
  ),
)

const selectedEvidenceGeoJson = computed<FeatureCollection | null>(() =>
  mergeFeatureCollections(props.selectedProjectPlantingGeoJson),
)

const selectedPlasticGeoJson = computed<FeatureCollection | null>(() =>
  mergeFeatureCollections(props.selectedProjectPlasticCollectionGeoJson),
)

const imagePinsGeoJson = computed<FeatureCollection<Point, IImagePinProperties>>(() => {
  const features: Feature<Point, IImagePinProperties>[] = []

  props.selectedProjectEvidence?.evidences.forEach((evidence) => {
    if (evidence.type !== 'PlantingSession') return

    const session = evidence as IPlantingSession
    const groups: Array<{
      longitude: number
      latitude: number
      fieldUpdateId?: number
      datePlanted?: string | null
      images: IImageGroupItem[]
    }> = []

    const processMedia = (mediaItems: IComponentMediaAsset[] | undefined, isVideo: boolean) => {
      mediaItems?.forEach((item) => {
        const longitude = item.longitude ?? session.longitude
        const latitude = item.latitude ?? session.latitude

        if (
          longitude == null ||
          latitude == null ||
          !Number.isFinite(latitude) ||
          !Number.isFinite(longitude)
        ) {
          return
        }

        let group = groups.find(
          (candidate) =>
            haversineMeters(latitude, longitude, candidate.latitude, candidate.longitude) <=
            IMAGE_STACK_DISTANCE_METERS,
        )

        if (!group) {
          group = {
            longitude,
            latitude,
            fieldUpdateId: session.fieldUpdateId,
            datePlanted: session.planting.datePlanted,
            images: [],
          }
          groups.push(group)
        }

        group.images.push({
          imageUrl: item.url,
          imagePreviewUrl: item.previewUrl || item.url,
          imageAlt: item.alt,
          isVideo,
        })
      })
    }

    processMedia(session.planting.images, false)
    processMedia(session.planting.videos, true)
    processMedia(session.siteAssessment.images, false)
    processMedia(session.siteAssessment.videos, true)

    groups.forEach((group) => {
      const stackCount = group.images.length

      if (stackCount === 1) {
        const image = group.images[0]

        features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [group.longitude, group.latitude],
          },
          properties: {
            type: 'image',
            fieldUpdateId: group.fieldUpdateId,
            imageUrl: image?.imageUrl,
            imagePreviewUrl: image?.imagePreviewUrl,
            imageAlt: image?.imageAlt,
            isVideo: image?.isVideo,
            datePlanted: group.datePlanted,
            stackCount: 1,
          },
        })
        return
      }

      if (stackCount > 1) {
        features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [group.longitude, group.latitude],
          },
          properties: {
            type: 'image-stack',
            fieldUpdateId: group.fieldUpdateId,
            datePlanted: group.datePlanted,
            stackCount,
            imagesJson: JSON.stringify(group.images),
          },
        })
      }
    })
  })

  return {
    type: 'FeatureCollection',
    features,
  }
})

const sitePinsGeoJson = computed<FeatureCollection<Point>>(() => {
  const features: Feature<Point, ISitePinProperties>[] = []

  props.selectedProjectEvidence?.evidences.forEach((evidence) => {
    if (
      evidence.type !== 'PlantingSession' ||
      evidence.latitude == null ||
      evidence.longitude == null ||
      !Number.isFinite(evidence.latitude) ||
      !Number.isFinite(evidence.longitude)
    ) {
      return
    }

    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [evidence.longitude, evidence.latitude],
      },
      properties: {
        type: 'site',
        fieldUpdateId: evidence.fieldUpdateId,
        siteId: evidence.site?.id,
      },
    })
  })

  return {
    type: 'FeatureCollection',
    features,
  }
})

const siteGeofencesGeoJson = computed<FeatureCollection>(() => {
  const features: Feature<Polygon | MultiPolygon>[] = []
  const sitesMap = new Map<string, { id: string; geofences: MultiPolygon }>()

  props.selectedProjectEvidence?.evidences.forEach((evidence) => {
    if (evidence.type !== 'PlantingSession') return

    const session = evidence as IPlantingSession

    if (session.site?.id && session.site.geofences) {
      sitesMap.set(session.site.id, {
        id: session.site.id,
        geofences: session.site.geofences,
      })
    }
  })

  sitesMap.forEach((site) => {
    features.push({
      type: 'Feature',
      geometry: site.geofences,
      properties: {
        siteId: site.id,
      },
    })
  })

  return {
    type: 'FeatureCollection',
    features,
  }
})

const plasticCollectionPinsGeoJson = computed<
  FeatureCollection<Point, IPlasticCollectionPinProperties>
>(() => {
  const features: Feature<Point, IPlasticCollectionPinProperties>[] = []
  const buyerGroups = new Map<
    string,
    {
      longitude: number
      latitude: number
      buyerName: string
      totalBottles: number
      collectionIndices: number[]
    }
  >()

  props.selectedProjectEvidence?.evidences.forEach((evidence, evidenceIndex) => {
    if (evidence.type !== 'PlasticCollection') return

    const collection = evidence as IPlasticCollection
    if (!collection.buyerName) return

    collection.geoJSON?.features?.forEach((feature) => {
      if (
        feature.geometry.type !== 'Point' ||
        !Array.isArray(feature.geometry.coordinates) ||
        feature.geometry.coordinates.length < 2
      ) {
        return
      }

      const [longitude, latitude] = feature.geometry.coordinates
      if (
        longitude == null ||
        latitude == null ||
        !Number.isFinite(longitude) ||
        !Number.isFinite(latitude)
      ) {
        return
      }

      const existing = buyerGroups.get(collection.buyerName)
      if (existing) {
        existing.totalBottles += collection.numberOfPlasticBottlesRescued ?? 0
        existing.collectionIndices.push(evidenceIndex)
        return
      }

      buyerGroups.set(collection.buyerName, {
        longitude,
        latitude,
        buyerName: collection.buyerName,
        totalBottles: collection.numberOfPlasticBottlesRescued ?? 0,
        collectionIndices: [evidenceIndex],
      })
    })
  })

  buyerGroups.forEach((group) => {
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [group.longitude, group.latitude],
      },
      properties: {
        type: 'plastic-collection',
        buyerName: group.buyerName,
        totalBottles: group.totalBottles,
        collectionIndices: JSON.stringify(group.collectionIndices),
      },
    })
  })

  return {
    type: 'FeatureCollection',
    features,
  }
})

const accessToken = computed(() => import.meta.env.VITE_MAPBOX_KEY)

const showInteractiveMap = computed(
  () =>
    Boolean(accessToken.value) &&
    mapboxSupported.value &&
    !hasMapError.value &&
    allowMapMount.value,
)

function updateAllowMapMount(): void {
  const el = mapShellRef.value
  if (!el) {
    allowMapMount.value = false
    return
  }
  allowMapMount.value = el.clientWidth > 0 && el.clientHeight > 0
}

watch(
  showInteractiveMap,
  (show) => {
    if (!show || mapInitBreadcrumbLogged) return
    mapInitBreadcrumbLogged = true
    const el = mapShellRef.value
    const nav = navigator as Navigator & { deviceMemory?: number }
    Sentry.addBreadcrumb({
      category: 'map',
      message: 'Public Mapbox: mounting interactive map',
      level: 'info',
      data: {
        mapbox_supported: mapboxSupported.value,
        container_width: el?.clientWidth,
        container_height: el?.clientHeight,
        visibility_state: typeof document !== 'undefined' ? document.visibilityState : undefined,
        device_memory: nav.deviceMemory,
      },
    })
  },
  { flush: 'post' },
)

onErrorCaptured((err, _instance, info) => {
  const message = err instanceof Error ? err.message : String(err)
  if (/Failed to initialize WebGL/i.test(message)) {
    hasMapError.value = true
    Sentry.addBreadcrumb({
      category: 'map',
      message: 'Public Mapbox: WebGL constructor error (handled)',
      level: 'error',
      data: { component_hook: info },
    })
    Sentry.captureMessage('Public page map: WebGL init fallback', {
      level: 'info',
      tags: {
        map_fallback_reason: 'webgl_init',
        mapbox_supported: String(mapboxSupported.value),
      },
    })
    return false
  }
  return true
})

onMounted(() => {
  void nextTick(() => {
    updateAllowMapMount()
    if (typeof ResizeObserver === 'undefined') {
      if (!allowMapMount.value) {
        allowMapMount.value = true
      }
      return
    }
    const el = mapShellRef.value
    if (!el) return
    resizeObserver = new ResizeObserver(() => {
      updateAllowMapMount()
    })
    resizeObserver.observe(el)
  })
})

const onMarkerClick = (projectId: string) => {
  emit('selectProject', projectId)
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const formatNumber = (value: number): string => new Intl.NumberFormat().format(value)

const formatDate = (value: string | null | undefined): string => {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return new Intl.DateTimeFormat(tolgee.value.getLanguage(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

const formatImpactNumber = (impactType: ImpactType, count: number): string => {
  const formattedCount = formatTotalAmounts(
    new Decimal(count).dividedBy(impactType === 'carbon' ? 1000 : 1).toNumber(),
  )

  return t.value(`PublicProjectCard.${impactType}`, { count: formattedCount })
}

const getPopupTagStyle = (): string => {
  const activeProject = selectedProject.value
  if (!activeProject) return ''

  const colors = props.colorSettings[activeProject.type]
  if (!colors) return ''

  return `style="background:${escapeHtml(colors.backgroundColor)};color:${escapeHtml(colors.textColor)};"`
}

const getPopupContainerStyle = (): string =>
  `style="border-color:${escapeHtml(selectedProjectColor.value)};"`

const closeImagePopup = () => {
  imagePopup.value?.remove()
  imagePopup.value = null
}

const closeSitePopup = () => {
  sitePopup.value?.remove()
  sitePopup.value = null
}

const closeCollectionPopup = () => {
  collectionPopup.value?.remove()
  collectionPopup.value = null
}

const closeAllPopups = () => {
  closeImagePopup()
  closeSitePopup()
  closeCollectionPopup()
}

const createMapPopup = (
  lngLat: mapboxgl.LngLat | [number, number],
  html: string,
): mapboxgl.Popup | null => {
  if (!map.value) return null

  const coordinates: [number, number] = Array.isArray(lngLat) ? lngLat : [lngLat.lng, lngLat.lat]

  return new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: true,
    anchor: 'bottom',
    offset: [0, -10],
    maxWidth: 'none',
  } as mapboxgl.PopupOptions)
    .setLngLat(coordinates)
    .setHTML(html)
    .addTo(map.value as unknown as Parameters<mapboxgl.Popup['addTo']>[0])
}

const buildPopupChevron = (direction: 'left' | 'right', popupId: string): string => {
  const path = direction === 'left' ? 'M15 18L9 12L15 6' : 'M9 18L15 12L9 6'

  return `<button class="map-popup-carousel-chevron map-popup-carousel-chevron-${direction}" data-popup-id="${popupId}" type="button">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="${path}" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  </button>`
}

const buildPopupCarouselWrapper = (options: {
  popupId: string
  itemsHtml: string
  showChevrons: boolean
  carouselClass?: string
  wrapperClass?: string
}): string => {
  const carouselClass = options.carouselClass ? ` ${options.carouselClass}` : ''
  const wrapperClass = options.wrapperClass ? ` ${options.wrapperClass}` : ''
  const leftChevron = options.showChevrons ? buildPopupChevron('left', options.popupId) : ''
  const rightChevron = options.showChevrons ? buildPopupChevron('right', options.popupId) : ''

  return `
    <div class="map-popup-carousel-wrapper${wrapperClass}">
      ${leftChevron}
      <div class="map-popup-carousel${carouselClass}" data-popup-id="${options.popupId}">
        ${options.itemsHtml}
      </div>
      ${rightChevron}
    </div>
  `
}

const attachCarouselChevronHandlers = (
  popupElement: HTMLElement,
  carousel: HTMLElement,
  scrollStep: number,
) => {
  const leftButton = popupElement.querySelector(
    '.map-popup-carousel-chevron-left',
  ) as HTMLButtonElement | null
  const rightButton = popupElement.querySelector(
    '.map-popup-carousel-chevron-right',
  ) as HTMLButtonElement | null

  if (!leftButton || !rightButton) return

  const updateChevronState = () => {
    const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth
    const canScrollLeft = carousel.scrollLeft > 0
    const canScrollRight = carousel.scrollLeft < maxScrollLeft - 1

    leftButton.disabled = !canScrollLeft
    rightButton.disabled = !canScrollRight
    leftButton.classList.toggle('map-popup-carousel-chevron--disabled', !canScrollLeft)
    rightButton.classList.toggle('map-popup-carousel-chevron--disabled', !canScrollRight)
  }

  leftButton.addEventListener('click', () => {
    if (leftButton.disabled) return
    carousel.scrollBy({ left: -scrollStep, behavior: 'smooth' })
    window.setTimeout(updateChevronState, 200)
  })

  rightButton.addEventListener('click', () => {
    if (rightButton.disabled) return
    carousel.scrollBy({ left: scrollStep, behavior: 'smooth' })
    window.setTimeout(updateChevronState, 200)
  })

  carousel.addEventListener('scroll', updateChevronState)
  window.setTimeout(updateChevronState, 0)
}

const setupPopupCarousel = (
  popupId: string,
  carouselSelector = '.map-popup-carousel',
  scrollStep?: number,
) => {
  const popupElement = document.getElementById(popupId)
  if (!popupElement) return

  const carousel = popupElement.querySelector(carouselSelector) as HTMLElement | null
  if (!carousel) return

  attachCarouselChevronHandlers(popupElement, carousel, scrollStep ?? 68)
}

const parseCarouselImagesJson = (imagesJson?: string): IImageGroupItem[] => {
  if (!imagesJson) return []

  try {
    const parsed = JSON.parse(imagesJson) as unknown
    return Array.isArray(parsed) ? (parsed as IImageGroupItem[]) : []
  } catch {
    return []
  }
}

const imageItemsFromProperties = (
  properties: IImagePinProperties | undefined,
): IImageGroupItem[] => {
  if (!properties) return []

  if (properties.type === 'image-stack') {
    return parseCarouselImagesJson(properties.imagesJson)
  }

  if (!properties.imageUrl && !properties.imagePreviewUrl) return []

  return [
    {
      imageUrl: properties.imageUrl,
      imagePreviewUrl: properties.imagePreviewUrl,
      imageAlt: properties.imageAlt,
      isVideo: properties.isVideo,
    },
  ]
}

const attachPopupGalleryHandlers = (popupId: string, items: IImageGroupItem[]): void => {
  const galleryItems = toGalleryItems(
    items.map((item) => ({
      url: item.imageUrl || item.imagePreviewUrl || '',
      previewUrl: item.imagePreviewUrl || item.imageUrl,
      alt: item.imageAlt,
      isVideo: item.isVideo,
    })),
  )
  if (!galleryItems.length) return

  void sharedMediaGallery.prewarm()

  window.setTimeout(() => {
    const popupElement = document.getElementById(popupId)
    if (!popupElement) return

    const links = popupElement.querySelectorAll<HTMLElement>('.map-popup-carousel-item')
    links.forEach((link, index) => {
      link.addEventListener('click', (event) => {
        event.preventDefault()
        void sharedMediaGallery.openGallery(galleryItems, index)
      })
    })
  }, 0)
}

const createMediaCardHtml = (item: IImageGroupItem): string => {
  const mediaUrl = item.imageUrl || item.imagePreviewUrl || ''
  const previewUrl = item.imagePreviewUrl || item.imageUrl || ''
  const alt = item.imageAlt || t.value('PublicMapPopup.planting_image')

  return `
    <a
      href="${escapeHtml(mediaUrl)}"
      target="_blank"
      rel="noreferrer"
      class="map-popup-carousel-item"
    >
      <div class="map-popup-carousel-media-wrapper">
        <img
          src="${escapeHtml(previewUrl)}"
          alt="${escapeHtml(alt)}"
          class="map-popup-carousel-image"
        />
        ${
          item.isVideo
            ? '<div class="map-popup-carousel-video-overlay"><span class="map-popup-carousel-video-icon"></span></div>'
            : ''
        }
      </div>
    </a>
  `
}

const showImagePopup = (lngLat: mapboxgl.LngLat, properties: IImagePinProperties) => {
  const imageUrl = properties.imageUrl || properties.imagePreviewUrl || ''
  const imagePreviewUrl = properties.imagePreviewUrl || properties.imageUrl || ''
  const imageAlt = properties.imageAlt || t.value('PublicMapPopup.planting_image')
  const dateText = properties.datePlanted
    ? t.value('PublicMapPopup.planting_session_date', {
        date: formatDate(properties.datePlanted),
      })
    : t.value('PublicMapPopup.planting_session')
  const popupId = `image-popup-${Date.now()}`

  closeAllPopups()

  const popup = createMapPopup(
    lngLat,
    `
      <div class="image-popup-container" id="${popupId}" ${getPopupContainerStyle()}>
        <a
          href="${escapeHtml(imageUrl)}"
          target="_blank"
          rel="noreferrer"
          class="map-popup-carousel-item"
        >
          <div class="map-popup-carousel-media-wrapper">
            <img src="${escapeHtml(imagePreviewUrl)}" alt="${escapeHtml(imageAlt)}" class="image-popup-image" />
            ${
              properties.isVideo
                ? '<div class="map-popup-carousel-video-overlay"><span class="map-popup-carousel-video-icon"></span></div>'
                : ''
            }
          </div>
        </a>
        <div class="image-popup-date">${escapeHtml(dateText)}</div>
      </div>
    `,
  )

  if (!popup) return
  imagePopup.value = popup
  rerenderOpenPopup = () => showImagePopup(lngLat, properties)
  attachPopupGalleryHandlers(popupId, imageItemsFromProperties(properties))
}

const showClusterPopup = (lngLat: mapboxgl.LngLat, images: IImageGroupItem[]) => {
  const popupId = `cluster-popup-${Date.now()}`
  const imagesHtml = images.map((image) => createMediaCardHtml(image)).join('')
  const popup = createMapPopup(
    lngLat,
    `
      <div class="map-popup-container" id="${popupId}" ${getPopupContainerStyle()}>
        <div class="map-popup-title">${escapeHtml(t.value('PublicMapPopup.planting_session'))}</div>
        <div class="map-popup-activity">${escapeHtml(
          t.value('PublicMapPopup.activity_count', { count: images.length }),
        )}</div>
        ${buildPopupCarouselWrapper({
          popupId,
          itemsHtml: imagesHtml,
          showChevrons: images.length >= 4,
        })}
      </div>
    `,
  )

  if (!popup) return

  closeAllPopups()
  imagePopup.value = popup
  rerenderOpenPopup = () => showClusterPopup(lngLat, images)
  attachPopupGalleryHandlers(popupId, images)
  window.setTimeout(() => setupPopupCarousel(popupId), 0)
}

const getPlantingSessionMedia = (session: IPlantingSession): IImageGroupItem[] => [
  ...(session.planting?.images ?? []).map((image) => ({
    imageUrl: image.url,
    imagePreviewUrl: image.previewUrl,
    imageAlt: image.alt ?? undefined,
    isVideo: false,
  })),
  ...(session.planting?.videos ?? []).map((video) => ({
    imageUrl: video.url,
    imagePreviewUrl: video.previewUrl,
    imageAlt: video.alt ?? undefined,
    isVideo: true,
  })),
]

const showSitePopupByFieldUpdateId = (fieldUpdateId: number, lngLat: mapboxgl.LngLat) => {
  const session = props.selectedProjectEvidence?.evidences.find(
    (evidence): evidence is IPlantingSession =>
      evidence.type === 'PlantingSession' && evidence.fieldUpdateId === fieldUpdateId,
  )

  if (!session) return

  const mediaItems = getPlantingSessionMedia(session)
  const popupId = `site-popup-${fieldUpdateId}`
  const amountPlanted = session.planting?.amountPlanted ?? 0
  const title = session.planting?.datePlanted
    ? t.value('PublicMapPopup.planting_session_date', {
        date: formatDate(session.planting.datePlanted),
      })
    : t.value('PublicMapPopup.planting_session')
  const tagText = selectedProject.value
    ? formatImpactNumber(selectedProject.value.type, amountPlanted)
    : ''
  const popup = createMapPopup(
    lngLat,
    `
      <div class="map-popup-container" id="${popupId}" ${getPopupContainerStyle()}>
        ${
          tagText
            ? `<div class="map-popup-tag" ${getPopupTagStyle()}><span class="map-popup-tag-text">${escapeHtml(tagText)}</span></div>`
            : ''
        }
        <div class="map-popup-title">${escapeHtml(title)}</div>
        <div class="map-popup-activity">${escapeHtml(
          t.value('PublicMapPopup.activity_count', { count: mediaItems.length }),
        )}</div>
        ${buildPopupCarouselWrapper({
          popupId,
          itemsHtml: mediaItems.map((item) => createMediaCardHtml(item)).join(''),
          showChevrons: mediaItems.length >= 4,
        })}
      </div>
    `,
  )

  if (!popup) return

  closeAllPopups()
  sitePopup.value = popup
  rerenderOpenPopup = () => showSitePopupByFieldUpdateId(fieldUpdateId, lngLat)
  attachPopupGalleryHandlers(popupId, mediaItems)
  window.setTimeout(() => setupPopupCarousel(popupId), 0)
}

const createPlasticCollectionCardHtml = (collection: IPlasticCollection): string => {
  const dateCreated = formatDate(collection.createdAt)
  const title = dateCreated
    ? `${t.value('PublicMapPopup.collection')} - ${dateCreated}`
    : t.value('PublicMapPopup.collection')

  return `
    <div class="map-popup-collection-item">
      <div class="map-popup-title">${escapeHtml(title)}</div>
      <div class="map-popup-details">
        ${
          collection.collectorName
            ? `<div class="map-popup-detail-item">
                <span class="map-popup-detail-label">${escapeHtml(t.value('PublicMapPopup.collector'))}</span>
                <span class="map-popup-detail-value">${escapeHtml(collection.collectorName)}</span>
              </div>`
            : ''
        }
        <div class="map-popup-detail-item">
          <span class="map-popup-detail-label">${escapeHtml(t.value('PublicMapPopup.plastic_bottles'))}</span>
          <span class="map-popup-detail-value">${escapeHtml(
            formatNumber(collection.numberOfPlasticBottlesRescued ?? 0),
          )}</span>
        </div>
        ${
          collection.materialType
            ? `<div class="map-popup-detail-item">
                <span class="map-popup-detail-label">${escapeHtml(t.value('PublicMapPopup.material'))}</span>
                <span class="map-popup-detail-value">${escapeHtml(collection.materialType)}</span>
              </div>`
            : ''
        }
        ${
          collection.weight != null
            ? `<div class="map-popup-detail-item">
                <span class="map-popup-detail-label">${escapeHtml(t.value('PublicMapPopup.weight'))}</span>
                <span class="map-popup-detail-value">${escapeHtml(formatNumber(collection.weight))} kg</span>
              </div>`
            : ''
        }
        ${
          collection.externalId
            ? `<div class="map-popup-detail-item">
                <span class="map-popup-detail-label">${escapeHtml(
                  t.value('PublicMapPopup.blockchain_record'),
                )}</span>
                <span class="map-popup-detail-value">${escapeHtml(collection.externalId)}</span>
              </div>`
            : ''
        }
      </div>
    </div>
  `
}

const createPlasticCollectionPopupContent = (collection: IPlasticCollection): string => {
  const dateCreated = formatDate(collection.createdAt)
  const title = dateCreated
    ? `${t.value('PublicMapPopup.collection')} - ${dateCreated}`
    : t.value('PublicMapPopup.collection')

  return `
    <div class="map-popup-container" ${getPopupContainerStyle()}>
      <div class="map-popup-tag map-popup-tag--plastic">
        <span class="map-popup-tag-text">${escapeHtml(
          formatImpactNumber('plastic', collection.numberOfPlasticBottlesRescued ?? 0),
        )}</span>
      </div>
      <div class="map-popup-title">${escapeHtml(title)}</div>
      <div class="map-popup-details">
        ${
          collection.collectorName
            ? `<div class="map-popup-detail-item">
                <span class="map-popup-detail-label">${escapeHtml(t.value('PublicMapPopup.collector'))}</span>
                <span class="map-popup-detail-value">${escapeHtml(collection.collectorName)}</span>
              </div>`
            : ''
        }
        ${
          collection.buyerName
            ? `<div class="map-popup-detail-item">
                <span class="map-popup-detail-label">${escapeHtml(t.value('PublicMapPopup.branch'))}</span>
                <span class="map-popup-detail-value">${escapeHtml(collection.buyerName)}</span>
              </div>`
            : ''
        }
        ${
          collection.materialType
            ? `<div class="map-popup-detail-item">
                <span class="map-popup-detail-label">${escapeHtml(t.value('PublicMapPopup.material'))}</span>
                <span class="map-popup-detail-value">${escapeHtml(collection.materialType)}</span>
              </div>`
            : ''
        }
        ${
          collection.weight != null
            ? `<div class="map-popup-detail-item">
                <span class="map-popup-detail-label">${escapeHtml(t.value('PublicMapPopup.weight'))}</span>
                <span class="map-popup-detail-value">${escapeHtml(formatNumber(collection.weight))} kg</span>
              </div>`
            : ''
        }
        ${
          collection.externalId
            ? `<div class="map-popup-detail-item">
                <span class="map-popup-detail-label">${escapeHtml(
                  t.value('PublicMapPopup.blockchain_record'),
                )}</span>
                <span class="map-popup-detail-value">${escapeHtml(collection.externalId)}</span>
              </div>`
            : ''
        }
      </div>
    </div>
  `
}

const showPlasticCollectionBuyerPopup = (
  lngLat: mapboxgl.LngLat,
  collections: IPlasticCollection[],
  buyerName: string,
  totalBottles: number,
) => {
  if (collections.length === 1) {
    const popup = createMapPopup(lngLat, createPlasticCollectionPopupContent(collections[0]))

    if (!popup) return

    closeAllPopups()
    collectionPopup.value = popup
    rerenderOpenPopup = () =>
      showPlasticCollectionBuyerPopup(lngLat, collections, buyerName, totalBottles)
    return
  }

  const popupId = `plastic-buyer-popup-${Date.now()}`
  const popup = createMapPopup(
    lngLat,
    `
      <div class="map-popup-container" id="${popupId}" ${getPopupContainerStyle()}>
        <div class="map-popup-tag map-popup-tag--plastic">
          <span class="map-popup-tag-text">${escapeHtml(formatImpactNumber('plastic', totalBottles))}</span>
        </div>
        <div class="map-popup-title">${escapeHtml(buyerName)}</div>
        <div class="map-popup-activity">${escapeHtml(
          t.value('PublicMapPopup.collections_count', { count: collections.length }),
        )}</div>
        ${buildPopupCarouselWrapper({
          popupId,
          itemsHtml: collections
            .map((collection) => createPlasticCollectionCardHtml(collection))
            .join(''),
          showChevrons: collections.length > 1,
          carouselClass: 'map-popup-collection-carousel',
          wrapperClass: 'map-popup-collection-carousel-wrapper',
        })}
      </div>
    `,
  )

  if (!popup) return

  closeAllPopups()
  collectionPopup.value = popup
  rerenderOpenPopup = () =>
    showPlasticCollectionBuyerPopup(lngLat, collections, buyerName, totalBottles)
  window.setTimeout(() => {
    const popupElement = document.getElementById(popupId)
    const carousel = popupElement?.querySelector(
      '.map-popup-collection-carousel',
    ) as HTMLElement | null
    if (popupElement && carousel) {
      attachCarouselChevronHandlers(popupElement, carousel, carousel.offsetWidth - 16)
    }
  }, 0)
}

watch(
  () => tolgee.value.getLanguage(),
  () => {
    const isOpen = [imagePopup, sitePopup, collectionPopup].some((popup) => popup.value?.isOpen())
    if (isOpen) void nextTick(() => rerenderOpenPopup?.())
  },
)

const runWhenMapReady = (callback: () => void) => {
  const mapInstance = map.value

  if (!mapInstance) return

  if (mapInstance.isStyleLoaded()) {
    callback()
    return
  }

  mapInstance.once('idle', callback)
}

const getBoundsFromCollections = (
  collections: Array<FeatureCollection | null | undefined>,
): mapboxgl.LngLatBounds | null => {
  const bounds = new mapboxgl.LngLatBounds()
  let hasCoordinates = false

  collections.forEach((collection) => {
    collection?.features.forEach((feature) => {
      getFeatureCoordinates(feature).forEach((coordinates) => {
        bounds.extend(coordinates)
        hasCoordinates = true
      })
    })
  })

  return hasCoordinates ? bounds : null
}

const fitMapToProjectPins = () => {
  if (hasFittedInitialBounds.value || !projectPins.value.length) {
    return
  }

  const bounds = new mapboxgl.LngLatBounds()

  projectPins.value.forEach((pin) => {
    if (!pin.pinLocation) return

    bounds.extend([pin.pinLocation.longitude, pin.pinLocation.latitude])
  })

  if (bounds.isEmpty()) {
    return
  }

  map.value?.fitBounds(bounds, {
    padding: { top: 60, right: 60, bottom: 200, left: 60 },
    duration: 0,
    maxZoom: 1.85,
  })

  hasFittedInitialBounds.value = true
}

const syncGeoJsonSource = (sourceId: string, data: FeatureCollection | null) => {
  const mapInstance = map.value

  if (!mapInstance) return

  const safeData = data && data.features.length ? data : EMPTY_FEATURE_COLLECTION
  const source = mapInstance.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined

  if (source) {
    source.setData(safeData)
    return
  }

  mapInstance.addSource(sourceId, {
    type: 'geojson',
    data: safeData,
  })
}

const removeLayerIfExists = (layerId: string) => {
  if (map.value?.getLayer(layerId)) {
    map.value.removeLayer(layerId)
  }
}

const removeSourceIfExists = (sourceId: string) => {
  if (map.value?.getSource(sourceId)) {
    map.value.removeSource(sourceId)
  }
}

const ensureMapImageLoaded = (iconId: string, imageSrc: string): Promise<void> => {
  const mapInstance = map.value

  if (!mapInstance || mapInstance.hasImage(iconId)) {
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    const image = new Image(32, 32)
    image.onload = () => {
      if (!mapInstance.hasImage(iconId)) {
        mapInstance.addImage(iconId, image)
      }
      resolve()
    }
    image.onerror = () => resolve()
    image.src = imageSrc
  })
}

const ensureLineLayer = (layerId: string, sourceId: string) => {
  const mapInstance = map.value

  if (!mapInstance || mapInstance.getLayer(layerId)) return

  mapInstance.addLayer({
    id: layerId,
    type: 'line',
    source: sourceId,
    filter: ['==', ['geometry-type'], 'LineString'],
    paint: {
      'line-color': selectedProjectColor.value,
      'line-width': 4,
      'line-opacity': 0.9,
    },
  })
}

const ensurePolygonOutlineLayer = (
  layerId: string,
  sourceId: string,
  color = selectedProjectColor.value,
) => {
  const mapInstance = map.value

  if (!mapInstance || mapInstance.getLayer(layerId)) return

  mapInstance.addLayer({
    id: layerId,
    type: 'line',
    source: sourceId,
    paint: {
      'line-color': color,
      'line-width': 2,
      'line-opacity': 0.8,
    },
  })
}

const ensureFillLayer = (
  layerId: string,
  sourceId: string,
  color = selectedProjectColor.value,
  opacity = 0.16,
) => {
  const mapInstance = map.value

  if (!mapInstance || mapInstance.getLayer(layerId)) return

  mapInstance.addLayer({
    id: layerId,
    type: 'fill',
    source: sourceId,
    filter: ['==', ['geometry-type'], 'Polygon'],
    paint: {
      'fill-color': color,
      'fill-opacity': opacity,
    },
  })
}

const ensureCircleLayer = (
  layerId: string,
  sourceId: string,
  radius = 6,
  opacity = 1,
  color = selectedProjectColor.value,
) => {
  const mapInstance = map.value

  if (!mapInstance || mapInstance.getLayer(layerId)) return

  mapInstance.addLayer({
    id: layerId,
    type: 'circle',
    source: sourceId,
    filter: ['==', ['geometry-type'], 'Point'],
    paint: {
      'circle-color': color,
      'circle-radius': radius,
      'circle-opacity': opacity,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 2,
    },
  })
}

const ensureSymbolLayer = (
  layerId: string,
  sourceId: string,
  iconId: string,
  beforeId?: string,
  minzoom?: number,
) => {
  const mapInstance = map.value

  if (!mapInstance || mapInstance.getLayer(layerId)) return

  const layer: mapboxgl.SymbolLayerSpecification = {
    id: layerId,
    type: 'symbol',
    source: sourceId,
    filter: ['==', ['geometry-type'], 'Point'],
    layout: {
      'icon-image': iconId,
      'icon-size': 0.9,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  }

  if (typeof minzoom === 'number') {
    layer.minzoom = minzoom
  }

  mapInstance.addLayer(layer, beforeId)
}

const ensureClusterSource = (sourceId: string, data: FeatureCollection) => {
  const mapInstance = map.value

  if (!mapInstance) return

  const existingSource = mapInstance.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined
  if (existingSource) {
    existingSource.setData(data)
    return
  }

  mapInstance.addSource(sourceId, {
    type: 'geojson',
    data,
    cluster: true,
    clusterMaxZoom: 14,
    clusterRadius: 50,
  } as mapboxgl.GeoJSONSourceSpecification)
}

const ensureClusterCircleLayer = (
  layerId: string,
  sourceId: string,
  color: string,
  beforeId?: string,
) => {
  const mapInstance = map.value

  if (!mapInstance || mapInstance.getLayer(layerId)) return

  mapInstance.addLayer(
    {
      id: layerId,
      type: 'circle',
      source: sourceId,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': color,
        'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    },
    beforeId,
  )
}

const ensureClusterCountLayer = (layerId: string, sourceId: string, beforeId?: string) => {
  const mapInstance = map.value

  if (!mapInstance || mapInstance.getLayer(layerId)) return

  mapInstance.addLayer(
    {
      id: layerId,
      type: 'symbol',
      source: sourceId,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
      paint: {
        'text-color': '#fff',
      },
    },
    beforeId,
  )
}

const ensureImageClusterLayers = () => {
  const mapInstance = map.value

  if (!mapInstance || !mapInstance.getSource(IMAGE_CLUSTER_SOURCE_ID)) return

  ensureClusterCircleLayer(IMAGE_CLUSTER_LAYER_ID, IMAGE_CLUSTER_SOURCE_ID, clusterColor.value)
  ensureClusterCountLayer(IMAGE_CLUSTER_COUNT_LAYER_ID, IMAGE_CLUSTER_SOURCE_ID)

  if (!mapInstance.getLayer(IMAGE_UNCLUSTERED_LAYER_ID)) {
    mapInstance.addLayer({
      id: IMAGE_UNCLUSTERED_LAYER_ID,
      type: 'circle',
      source: IMAGE_CLUSTER_SOURCE_ID,
      filter: [
        'all',
        ['!', ['has', 'point_count']],
        ['>=', ['coalesce', ['get', 'stackCount'], 1], 2],
      ],
      paint: {
        'circle-color': clusterColor.value,
        'circle-radius': 12,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    })
  }

  if (!mapInstance.getLayer(IMAGE_UNCLUSTERED_STACK_COUNT_LAYER_ID)) {
    mapInstance.addLayer({
      id: IMAGE_UNCLUSTERED_STACK_COUNT_LAYER_ID,
      type: 'symbol',
      source: IMAGE_CLUSTER_SOURCE_ID,
      filter: [
        'all',
        ['!', ['has', 'point_count']],
        ['>=', ['coalesce', ['get', 'stackCount'], 1], 2],
      ],
      layout: {
        'text-field': ['to-string', ['get', 'stackCount']],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
      paint: {
        'text-color': '#fff',
      },
    })
  }

  ensureSymbolLayer(
    IMAGE_UNCLUSTERED_SINGLETON_LAYER_ID,
    IMAGE_CLUSTER_SOURCE_ID,
    IMAGE_PIN_ICON_ID,
  )
}

const ensureSiteClusterLayers = () => {
  const mapInstance = map.value

  if (!mapInstance || !mapInstance.getSource(SITE_PINS_SOURCE_ID)) return

  const beforeId = mapInstance.getLayer(IMAGE_CLUSTER_LAYER_ID) ? IMAGE_CLUSTER_LAYER_ID : undefined
  ensureClusterCircleLayer(SITE_CLUSTER_LAYER_ID, SITE_PINS_SOURCE_ID, clusterColor.value, beforeId)
  ensureClusterCountLayer(SITE_CLUSTER_COUNT_LAYER_ID, SITE_PINS_SOURCE_ID, beforeId)
  ensureSymbolLayer(SITE_PINS_LAYER_ID, SITE_PINS_SOURCE_ID, PLANTING_SITE_PIN_ICON_ID, beforeId, 8)
}

const ensurePlasticClusterLayers = () => {
  const mapInstance = map.value

  if (!mapInstance || !mapInstance.getSource(PLASTIC_CLUSTER_SOURCE_ID)) return

  const beforeId = mapInstance.getLayer(SITE_CLUSTER_LAYER_ID)
    ? SITE_CLUSTER_LAYER_ID
    : mapInstance.getLayer(IMAGE_CLUSTER_LAYER_ID)
      ? IMAGE_CLUSTER_LAYER_ID
      : undefined

  ensureClusterCircleLayer(
    PLASTIC_CLUSTER_LAYER_ID,
    PLASTIC_CLUSTER_SOURCE_ID,
    clusterColor.value,
    beforeId,
  )
  ensureClusterCountLayer(PLASTIC_CLUSTER_COUNT_LAYER_ID, PLASTIC_CLUSTER_SOURCE_ID, beforeId)
  ensureSymbolLayer(
    PLASTIC_UNCLUSTERED_LAYER_ID,
    PLASTIC_CLUSTER_SOURCE_ID,
    PLASTIC_COLLECTION_PIN_ICON_ID,
    beforeId,
  )
}

const removeImageClusterLayers = () => {
  removeLayerIfExists(IMAGE_UNCLUSTERED_SINGLETON_LAYER_ID)
  removeLayerIfExists(IMAGE_UNCLUSTERED_STACK_COUNT_LAYER_ID)
  removeLayerIfExists(IMAGE_UNCLUSTERED_LAYER_ID)
  removeLayerIfExists(IMAGE_CLUSTER_COUNT_LAYER_ID)
  removeLayerIfExists(IMAGE_CLUSTER_LAYER_ID)
  removeSourceIfExists(IMAGE_CLUSTER_SOURCE_ID)
}

const removeSiteClusterLayers = () => {
  removeLayerIfExists(SITE_PINS_LAYER_ID)
  removeLayerIfExists(SITE_CLUSTER_COUNT_LAYER_ID)
  removeLayerIfExists(SITE_CLUSTER_LAYER_ID)
  removeSourceIfExists(SITE_PINS_SOURCE_ID)
}

const removePlasticClusterLayers = () => {
  removeLayerIfExists(PLASTIC_UNCLUSTERED_LAYER_ID)
  removeLayerIfExists(PLASTIC_CLUSTER_COUNT_LAYER_ID)
  removeLayerIfExists(PLASTIC_CLUSTER_LAYER_ID)
  removeSourceIfExists(PLASTIC_CLUSTER_SOURCE_ID)
}

const hasClusterAtPoint = (point: mapboxgl.Point): boolean => {
  if (!map.value) return false

  const layerIds = [IMAGE_CLUSTER_LAYER_ID, SITE_CLUSTER_LAYER_ID, PLASTIC_CLUSTER_LAYER_ID].filter(
    (layerId) => map.value?.getLayer(layerId),
  )

  if (!layerIds.length) return false

  return map.value.queryRenderedFeatures(point, { layers: layerIds }).length > 0
}

const expandCluster = (event: mapboxgl.MapMouseEvent, layerId: string, sourceId: string) => {
  const mapInstance = map.value

  if (!mapInstance) return

  const features = mapInstance.queryRenderedFeatures(event.point, { layers: [layerId] })
  const clusterId = features[0]?.properties?.cluster_id
  const clusterFeature = features[0]

  if (!clusterId || !clusterFeature) return

  const source = mapInstance.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined
  if (!source) return

  const coordinates =
    clusterFeature.geometry.type === 'Point'
      ? clusterFeature.geometry.coordinates
      : [event.lngLat.lng, event.lngLat.lat]

  source.getClusterExpansionZoom(clusterId, (error, zoom) => {
    if (error || !map.value) return

    const currentZoom = map.value.getZoom()
    const targetZoom = zoom ?? currentZoom

    if (targetZoom > currentZoom + 0.01 && currentZoom < DEFAULT_MAX_ZOOM) {
      map.value.easeTo({
        center: [coordinates[0], coordinates[1]],
        zoom: targetZoom,
        duration: 2000,
      })
    }
  })
}

const registerCursorHandlers = (layerIds: string[]) => {
  const mapInstance = map.value

  if (!mapInstance) return

  const setCursor = (cursor: string) => {
    if (map.value) {
      map.value.getCanvas().style.cursor = cursor
    }
  }

  layerIds.forEach((layerId) => {
    mapInstance.on('mouseenter', layerId, () => setCursor('pointer'))
    mapInstance.on('mouseleave', layerId, () => setCursor(''))
  })
}

const setupImageClusterEventHandlers = () => {
  const mapInstance = map.value

  if (!mapInstance || hasImageClusterHandlers.value) return

  mapInstance.on('click', IMAGE_CLUSTER_LAYER_ID, (event) =>
    expandCluster(event, IMAGE_CLUSTER_LAYER_ID, IMAGE_CLUSTER_SOURCE_ID),
  )
  mapInstance.on('click', IMAGE_UNCLUSTERED_SINGLETON_LAYER_ID, (event) => {
    if (hasClusterAtPoint(event.point)) return

    const feature = mapInstance.queryRenderedFeatures(event.point, {
      layers: [IMAGE_UNCLUSTERED_SINGLETON_LAYER_ID],
    })[0]
    const properties = feature?.properties as IImagePinProperties | undefined

    if (properties?.type === 'image' && (properties.imageUrl || properties.imagePreviewUrl)) {
      showImagePopup(event.lngLat, properties)
    }
  })
  mapInstance.on('click', IMAGE_UNCLUSTERED_LAYER_ID, (event) => {
    if (hasClusterAtPoint(event.point)) return

    const features = mapInstance.queryRenderedFeatures(event.point, {
      layers: [IMAGE_UNCLUSTERED_LAYER_ID],
    })
    const firstProperties = features[0]?.properties as IImagePinProperties | undefined
    if (!firstProperties) return

    if (firstProperties.type === 'image-stack' && firstProperties.imagesJson) {
      const images = parseCarouselImagesJson(firstProperties.imagesJson)
      if (images.length) {
        showClusterPopup(event.lngLat, images)
      }
      return
    }

    const imageFeatures = features.flatMap((feature) =>
      imageItemsFromProperties(feature.properties as IImagePinProperties | undefined),
    )

    if (imageFeatures.length > 1) {
      showClusterPopup(event.lngLat, imageFeatures)
      return
    }

    if (
      firstProperties.type === 'image' &&
      (firstProperties.imageUrl || firstProperties.imagePreviewUrl)
    ) {
      showImagePopup(event.lngLat, firstProperties)
    }
  })
  registerCursorHandlers([
    IMAGE_CLUSTER_LAYER_ID,
    IMAGE_UNCLUSTERED_LAYER_ID,
    IMAGE_UNCLUSTERED_SINGLETON_LAYER_ID,
  ])
  hasImageClusterHandlers.value = true
}

const setupSiteClusterEventHandlers = () => {
  const mapInstance = map.value

  if (!mapInstance || hasSiteClusterHandlers.value) return

  mapInstance.on('click', SITE_CLUSTER_LAYER_ID, (event) =>
    expandCluster(event, SITE_CLUSTER_LAYER_ID, SITE_PINS_SOURCE_ID),
  )
  mapInstance.on('click', SITE_PINS_LAYER_ID, (event) => {
    if (hasClusterAtPoint(event.point)) return

    const feature = mapInstance.queryRenderedFeatures(event.point, {
      layers: [SITE_PINS_LAYER_ID],
    })[0]
    const properties = feature?.properties as ISitePinProperties | undefined

    if (typeof properties?.fieldUpdateId === 'number') {
      showSitePopupByFieldUpdateId(properties.fieldUpdateId, event.lngLat)
    }
  })
  registerCursorHandlers([SITE_CLUSTER_LAYER_ID, SITE_PINS_LAYER_ID])
  hasSiteClusterHandlers.value = true
}

const setupPlasticClusterEventHandlers = () => {
  const mapInstance = map.value

  if (!mapInstance || hasPlasticClusterHandlers.value) return

  mapInstance.on('click', PLASTIC_CLUSTER_LAYER_ID, (event) =>
    expandCluster(event, PLASTIC_CLUSTER_LAYER_ID, PLASTIC_CLUSTER_SOURCE_ID),
  )
  mapInstance.on('click', PLASTIC_UNCLUSTERED_LAYER_ID, (event) => {
    if (hasClusterAtPoint(event.point)) return

    const feature = mapInstance.queryRenderedFeatures(event.point, {
      layers: [PLASTIC_UNCLUSTERED_LAYER_ID],
    })[0]
    const properties = feature?.properties as IPlasticCollectionPinProperties | undefined
    if (!properties?.collectionIndices || !properties.buyerName) return

    try {
      const collectionIndices = JSON.parse(properties.collectionIndices) as number[]
      const collections = collectionIndices
        .map((index) => props.selectedProjectEvidence?.evidences[index])
        .filter(
          (evidence): evidence is IPlasticCollection => evidence?.type === 'PlasticCollection',
        )

      if (collections.length) {
        showPlasticCollectionBuyerPopup(
          event.lngLat,
          collections,
          properties.buyerName,
          properties.totalBottles ?? 0,
        )
      }
    } catch (error) {
      console.warn('Failed to parse plastic collection indices for public map popup', error)
    }
  })
  registerCursorHandlers([PLASTIC_CLUSTER_LAYER_ID, PLASTIC_UNCLUSTERED_LAYER_ID])
  hasPlasticClusterHandlers.value = true
}

const updateLayerColors = () => {
  const mapInstance = map.value

  if (!mapInstance) return

  const setColor = (
    layerId: string,
    paintProperty: 'circle-color' | 'line-color' | 'fill-color',
    value: string,
  ) => {
    if (!mapInstance.getLayer(layerId)) return
    mapInstance.setPaintProperty(layerId, paintProperty, value)
  }

  setColor(SELECTED_EVIDENCE_LINE_LAYER_ID, 'line-color', selectedProjectColor.value)
  setColor(SELECTED_EVIDENCE_FILL_LAYER_ID, 'fill-color', selectedProjectColor.value)
  setColor(SELECTED_EVIDENCE_POINT_LAYER_ID, 'circle-color', selectedProjectColor.value)
  setColor(IMAGE_CLUSTER_LAYER_ID, 'circle-color', clusterColor.value)
  setColor(IMAGE_UNCLUSTERED_LAYER_ID, 'circle-color', clusterColor.value)
  setColor(SITE_CLUSTER_LAYER_ID, 'circle-color', clusterColor.value)
  setColor(PLASTIC_CLUSTER_LAYER_ID, 'circle-color', clusterColor.value)
  setColor(SITE_GEOFENCES_FILL_LAYER_ID, 'fill-color', '#CBEFE1')
  setColor(SITE_GEOFENCES_OUTLINE_LAYER_ID, 'line-color', '#CBEFE1')
}

const updateSelectedSources = () => {
  runWhenMapReady(() => {
    syncGeoJsonSource(SELECTED_EVIDENCE_SOURCE_ID, selectedEvidenceGeoJson.value)
    syncGeoJsonSource(
      SITE_GEOFENCES_SOURCE_ID,
      siteGeofencesGeoJson.value.features.length
        ? mergeFeatureCollections([siteGeofencesGeoJson.value])
        : null,
    )

    ensureLineLayer(SELECTED_EVIDENCE_LINE_LAYER_ID, SELECTED_EVIDENCE_SOURCE_ID)
    ensureFillLayer(SELECTED_EVIDENCE_FILL_LAYER_ID, SELECTED_EVIDENCE_SOURCE_ID)
    ensureCircleLayer(SELECTED_EVIDENCE_POINT_LAYER_ID, SELECTED_EVIDENCE_SOURCE_ID, 7)
    ensureFillLayer(SITE_GEOFENCES_FILL_LAYER_ID, SITE_GEOFENCES_SOURCE_ID, '#CBEFE1', 0.2)
    ensurePolygonOutlineLayer(SITE_GEOFENCES_OUTLINE_LAYER_ID, SITE_GEOFENCES_SOURCE_ID, '#CBEFE1')

    if (imagePinsGeoJson.value.features.length) {
      void ensureMapImageLoaded(IMAGE_PIN_ICON_ID, imagePinIcon).then(() => {
        ensureClusterSource(IMAGE_CLUSTER_SOURCE_ID, imagePinsGeoJson.value)
        ensureImageClusterLayers()
        setupImageClusterEventHandlers()
      })
    } else {
      removeImageClusterLayers()
    }

    if (sitePinsGeoJson.value.features.length) {
      void ensureMapImageLoaded(PLANTING_SITE_PIN_ICON_ID, plantingSitePinIcon).then(() => {
        ensureClusterSource(SITE_PINS_SOURCE_ID, sitePinsGeoJson.value)
        ensureSiteClusterLayers()
        setupSiteClusterEventHandlers()
      })
    } else {
      removeSiteClusterLayers()
    }

    if (plasticCollectionPinsGeoJson.value.features.length) {
      void ensureMapImageLoaded(PLASTIC_COLLECTION_PIN_ICON_ID, collectionPinIcon).then(() => {
        ensureClusterSource(PLASTIC_CLUSTER_SOURCE_ID, plasticCollectionPinsGeoJson.value)
        ensurePlasticClusterLayers()
        setupPlasticClusterEventHandlers()
      })
    } else {
      removePlasticClusterLayers()
    }

    updateLayerColors()
  })
}

const focusSelectedProject = () => {
  runWhenMapReady(() => {
    map.value?.stop()

    const bounds = getBoundsFromCollections([
      selectedEvidenceGeoJson.value,
      selectedPlasticGeoJson.value,
    ])

    if (bounds) {
      map.value?.fitBounds(bounds, {
        padding: { top: 80, right: 80, bottom: 220, left: 80 },
        duration: PROJECT_FOCUS_DURATION_MS,
        maxZoom: 11,
        essential: true,
      })
      return
    }

    const selectedPin = projectPins.value.find((pin) => pin.projectId === props.selectedProjectId)
    if (!selectedPin?.pinLocation) return

    map.value?.flyTo({
      center: [selectedPin.pinLocation.longitude, selectedPin.pinLocation.latitude],
      zoom: 5,
      duration: PROJECT_FOCUS_DURATION_MS,
      essential: true,
    })
  })
}

const cleanupInteractiveMapState = (): void => {
  const instance = map.value
  if (instance && webglContextLostHandler) {
    try {
      instance.off('webglcontextlost', webglContextLostHandler)
    } catch {
      // Instance may already be destroyed by the child
    }
  }
  webglContextLostHandler = undefined
  closeAllPopups()
  map.value = null
  hasFittedInitialBounds.value = false
  hasImageClusterHandlers.value = false
  hasSiteClusterHandlers.value = false
  hasPlasticClusterHandlers.value = false
  lastHandledFocusRequestKey.value = 0
}

const handleMapCreated = (mapInstance: mapboxgl.Map) => {
  map.value = mapInstance
  webglContextLostHandler = (e: MapEventOf<'webglcontextlost'>) => {
    e.originalEvent?.preventDefault()
    hasMapError.value = true
    Sentry.addBreadcrumb({
      category: 'map',
      message: 'Public Mapbox: webglcontextlost',
      level: 'warning',
    })
  }
  mapInstance.on('webglcontextlost', webglContextLostHandler)
  mapInstance.on('error', (event: { error?: Error }) => {
    if (event.error) {
      console.error('Public Mapbox map error', event.error)
      hasMapError.value = true
    }
  })
  mapInstance.on('load', () => {
    hasMapError.value = false

    updateSelectedSources()
    fitMapToProjectPins()
  })
}

watch(showInteractiveMap, (show) => {
  if (show) return
  cleanupInteractiveMapState()
})

watch(projectPins, () => {
  if (!map.value) return

  fitMapToProjectPins()
})

watch(
  [
    selectedEvidenceGeoJson,
    selectedPlasticGeoJson,
    imagePinsGeoJson,
    sitePinsGeoJson,
    siteGeofencesGeoJson,
    plasticCollectionPinsGeoJson,
    () => props.selectedProjectId,
    selectedProjectColor,
    clusterColor,
  ],
  () => {
    if (!map.value) return

    updateSelectedSources()
  },
  { deep: true },
)

watch(
  () => props.focusRequestKey,
  (focusRequestKey) => {
    if (!map.value || !focusRequestKey || focusRequestKey === lastHandledFocusRequestKey.value)
      return

    lastHandledFocusRequestKey.value = focusRequestKey
    focusSelectedProject()
  },
)

watch(
  () => props.selectedProjectId,
  () => {
    closeAllPopups()
  },
)

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
  cleanupInteractiveMapState()
})
</script>

<template>
  <div ref="mapShellRef" class="public-mapbox-shell">
    <MapboxMap
      v-if="showInteractiveMap"
      :access-token="accessToken"
      :attribution-control="false"
      :box-zoom="true"
      :center="DEFAULT_MAP_CENTER"
      :cooperative-gestures="true"
      :double-click-zoom="true"
      :drag-pan="true"
      :drag-rotate="true"
      :interactive="true"
      :keyboard="true"
      :map-style="MAP_STYLE"
      :max-zoom="DEFAULT_MAX_ZOOM"
      :scroll-zoom="true"
      :touch-pitch="true"
      :touch-zoom-rotate="true"
      :zoom="DEFAULT_MAP_ZOOM"
      class="public-mapbox-canvas"
      projection="globe"
      @mb-created="handleMapCreated"
    >
      <template v-for="pin in projectPins" :key="`${pin.projectId}-${pin.index}`">
        <MapboxMarker
          v-if="pin.pinLocation"
          :anchor="'bottom'"
          :lng-lat="[pin.pinLocation.longitude, pin.pinLocation.latitude]"
        >
          <button
            :aria-pressed="pin.projectId === selectedProjectId"
            class="public-mapbox-marker"
            type="button"
            @click="onMarkerClick(pin.projectId)"
          >
            <PublicMapPin
              :is-active="pin.projectId === selectedProjectId"
              :is-animated="false"
              :is-static="true"
              :pin="pin"
            />
          </button>
        </MapboxMarker>
      </template>
    </MapboxMap>
    <img v-else alt="" class="public-mapbox-fallback" src="@/assets/images/map.webp" />
  </div>
</template>

<style lang="scss">
.image-popup-container {
  border-radius: 10px;
  border: 1px solid #6572ed;
  background: var(--colour-grey-10, #f9f9f9);
  box-shadow: 2px 2px 20px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  padding: 16px 20px;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  width: 164px;
}

.image-popup-container .map-popup-carousel-item {
  display: block;
  cursor: pointer;
  text-decoration: none;
  border: none;
  padding: 0;
  margin: 0;
}

.image-popup-image {
  width: 124px;
  height: 124px;
  border-radius: 10px;
  object-fit: cover;
  display: block;
}

.image-popup-date {
  color: var(--text-text-primary-light, #212121);
  font-family: Cabin, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.28px;
}

.mapboxgl-popup-content {
  padding: 0 !important;
  border-radius: 10px !important;
  width: 164px !important;
}

.mapboxgl-popup-content:has(.map-popup-container) {
  width: 268px !important;
}

.mapboxgl-popup-tip {
  display: none !important;
}

.public-mapbox-shell {
  width: 100%;
  height: 100%;
}

.public-mapbox-canvas {
  width: 100%;
  height: 500px;
  border-radius: 10px;
  overflow: hidden;

  @include media('sm-and-up') {
    height: 560px;
  }

  @include media('md-and-up') {
    height: 700px;
  }
}

.public-mapbox-fallback {
  width: 100%;
  height: 500px;
  display: block;
  object-fit: cover;
  border-radius: 10px;

  @include media('sm-and-up') {
    height: 560px;
  }

  @include media('md-and-up') {
    height: 700px;
  }
}

.public-mapbox-marker {
  border: 0;
  background: transparent;
  padding: 0;
  display: flex;
  cursor: pointer;
}

.public-mapbox-marker :deep(.public-map-pin) {
  position: relative !important;
  top: -10px;
  opacity: 1;
}

.public-mapbox-canvas :deep(.mapboxgl-canvas-container.mapboxgl-interactive),
.public-mapbox-canvas :deep(.mapboxgl-canvas) {
  touch-action: auto;
}

.public-mapbox-canvas .mapboxgl-ctrl-logo,
.public-mapbox-canvas a.mapboxgl-ctrl-logo {
  z-index: 0 !important;
  position: absolute;
  bottom: -99px;
  pointer-events: none;
  opacity: 0 !important;
}

.public-mapbox-canvas .mapboxgl-ctrl-bottom-left,
.public-mapbox-canvas .mapboxgl-ctrl-bottom-right,
.public-mapbox-canvas .mapboxgl-ctrl-attrib,
.public-mapbox-canvas .mapboxgl-compact,
.public-mapbox-canvas .mapboxgl-ctrl-attrib-button {
  display: none;
}

.map-popup-container {
  border-radius: 10px;
  border: 1px solid #6572ed;
  background: var(--colour-grey-10, #f9f9f9);
  box-shadow: 2px 2px 20px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  padding: 16px 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
  width: 268px;
}

.map-popup-tag {
  display: flex;
  padding: 4px 8px;
  align-items: flex-start;
  gap: 10px;
  border-radius: 8px;
}

.map-popup-tag--plastic {
  background: rgb(156, 210, 235);
}

.map-popup-tag-text {
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  color: var(--ui-black);
  letter-spacing: 0.2px;
}

.map-popup-title {
  color: var(--text-text-primary-light, #212121);
  font-family: Cabin, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.28px;
}

.map-popup-activity {
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.2px;
  color: var(--text-text-primary-light, #212121);
}

.map-popup-details {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px 24px;
  width: 100%;
}

.map-popup-detail-item {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1 1 calc(50% - 12px);
  min-width: 0;
}

.map-popup-detail-label {
  font-size: 12px;
  letter-spacing: 0.24px;
  color: #000;
  font-weight: 500;
}

.map-popup-detail-value {
  font-size: 12px;
  letter-spacing: 0.24px;
  color: #000;
  font-weight: 400;
}

.map-popup-carousel-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
}

.map-popup-collection-carousel-wrapper {
  margin-right: -20px;
  width: calc(100% + 20px);
}

.map-popup-carousel {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  width: 100%;
  padding: 0 4px;
  margin: 0 -4px;
}

.map-popup-carousel::-webkit-scrollbar {
  display: none;
}

.map-popup-carousel-item {
  text-decoration: none;
  flex: 0 0 60px;
}

.map-popup-carousel-wrapper:not(.map-popup-collection-carousel-wrapper) .map-popup-carousel {
  width: calc(100% - 8px);
}

.map-popup-collection-carousel {
  scroll-snap-type: x mandatory;
  scroll-padding: 0;
}

.map-popup-collection-item {
  flex: 0 0 calc(100% - 16px);
  scroll-snap-align: start;
  scroll-snap-stop: always;
  min-width: 0;
  margin-right: 5px;
  border: 1px solid #a9a9a9;
  border-radius: 5px;
  padding: 10px;
}

.map-popup-collection-item .map-popup-title {
  margin-bottom: 8px;
}

.map-popup-collection-item .map-popup-details {
  gap: 8px;
}

.map-popup-carousel-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

.map-popup-carousel-media-wrapper {
  position: relative;
  display: inline-block;
}

.map-popup-carousel-video-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  border-radius: 8px;
}

.map-popup-carousel-video-icon {
  position: relative;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.map-popup-carousel-video-icon::before {
  content: '';
  width: 0;
  height: 0;
  border-left: 8px solid #ffffff;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  margin-left: 1px;
}

.map-popup-carousel-chevron {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: #fff;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  user-select: none;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.map-popup-carousel-chevron--disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.map-popup-carousel-chevron-left {
  left: 0;
}

.map-popup-collection-carousel-wrapper .map-popup-carousel-chevron-left {
  left: -16px;
}

.map-popup-carousel-chevron-right {
  right: 8px;
}

.map-popup-collection-carousel-wrapper .map-popup-carousel-chevron-right {
  right: 4px;
}

.map-popup-carousel-chevron svg {
  width: 16px;
  height: 16px;
}
</style>
